import { useLayoutEffect, useState } from 'react';
import { ANCHOR_LOOKUP_INTERVAL_MS, findAnchor } from '../utils/tourSteps';

/** Fixed header height, so a scrolled-to anchor does not hide under it. */
const HEADER_OFFSET = 72;
const MARGIN = 8;

/**
 * Keeps the highlight inside the viewport.
 *
 * An anchor can be a whole section — a tournament list, a form — taller than
 * the screen. Its raw rect then starts far above the viewport and ends far
 * below, which makes the spotlight cutout swallow the entire page (nothing
 * looks dimmed) and pushes the popover off-screen, so the tour appears not to
 * run at all. Clipping to the visible band keeps both anchored to what the
 * manager can actually see.
 */
function clipToViewport(rect: DOMRect): DOMRect {
  const top = Math.max(rect.top, HEADER_OFFSET + MARGIN);
  const bottom = Math.min(rect.bottom, window.innerHeight - MARGIN);
  const left = Math.max(rect.left, MARGIN);
  const right = Math.min(rect.right, window.innerWidth - MARGIN);

  return new DOMRect(
    left,
    top,
    Math.max(0, right - left),
    Math.max(0, bottom - top),
  );
}

function isSameRect(a: DOMRect | null, b: DOMRect | null) {
  return (
    a === b ||
    (!!a &&
      !!b &&
      a.top === b.top &&
      a.left === b.left &&
      a.width === b.width &&
      a.height === b.height)
  );
}

/**
 * Brings the anchor up to just under the fixed header, and returns the undo
 * for the scroll margin that puts it there.
 *
 * Centering it split the free height into a band above and a band below, and
 * on a notebook or a phone neither band fits the popover, so it ended up
 * covering the very field it explains. Starting at the top leaves all that
 * height below the anchor. The margin stays until the step moves on: an
 * engine may work out a smooth scroll's target after this call (Gecko can
 * redo it on a later reflow), and with the margin gone by then the anchor
 * would land under the header.
 */
function scrollToAnchor(element: HTMLElement): () => void {
  const previous = element.style.scrollMarginTop;
  element.style.scrollMarginTop = `${HEADER_OFFSET + MARGIN}px`;
  element.scrollIntoView({ block: 'start', behavior: 'smooth' });
  return () => {
    element.style.scrollMarginTop = previous;
  };
}

/**
 * Live bounding rect of the element carrying `data-guide-id={anchor}`, clipped
 * to the viewport, or null while that element is not in the document.
 *
 * Measures every frame instead of on scroll and resize, because the anchor
 * also moves without either: data landing above it pushes it down, and a
 * re-render can swap it for a new node, which a one-time lookup never sees —
 * the tour stays up with nothing to point at.
 * The popover already re-places itself every frame, so this adds one rect
 * read per frame, and a render only when the rect actually changed.
 */
export function useAnchorRect(anchor: string | null): DOMRect | null {
  const [rect, setRect] = useState<DOMRect | null>(null);

  // A layout effect, so a new step does not paint once with its text at the
  // previous anchor and then jump.
  useLayoutEffect(() => {
    if (!anchor) {
      setRect(null);
      return;
    }

    let element: HTMLElement | null = null;
    // Compared here rather than in a state updater, which would queue an
    // update on every frame even while nothing moves.
    let measured: DOMRect | null | undefined;
    let restoreScrollMargin: (() => void) | null = null;
    let lastLookup = -Infinity;
    let frame = 0;

    const track = (now: number) => {
      // A detached node measures as an empty box in the corner.
      if (!element?.isConnected) element = null;
      if (!element && now - lastLookup >= ANCHOR_LOOKUP_INTERVAL_MS) {
        lastLookup = now;
        element = findAnchor(anchor);
        if (element) {
          restoreScrollMargin?.();
          restoreScrollMargin = scrollToAnchor(element);
        }
      }
      const next = element
        ? clipToViewport(element.getBoundingClientRect())
        : null;
      if (measured === undefined || !isSameRect(measured, next)) {
        measured = next;
        setRect(next);
      }
      frame = requestAnimationFrame(track);
    };
    track(performance.now());

    return () => {
      cancelAnimationFrame(frame);
      restoreScrollMargin?.();
    };
  }, [anchor]);

  return rect;
}
